import { getApps } from 'firebase/app'
import { auth } from '../../firebaseConfig'
import { breadcrumb } from '../../utils/sentry'
import { clearAuth } from '../../utils/auth'
import { useAuthStore } from '../../store/authStore'

const RETRY_TIMEOUT = 3 * 1000 // 3 sec
const REFRESH_TIMEOUT = 30 * 60 * 1000 // 30 min
const MIN_REFRESH_INTERVAL = 10 * 1000 // 10 sec
const EVENT_DEBOUNCE_DELAY = 500 // 500ms debounce for iOS events

class TokenRefresher {
    /**
     * TokenRefresher
     * ---------------
     * A singleton utility that keeps the Firebase ID token of the currently
     * authenticated user fresh throughout the lifetime of the page.
     *
     * Features
     * --------
     * 1. **Automatic refresh**
     *    • Forces a token refresh every 30 minutes (`REFRESH_TIMEOUT`).
     * 2. **Retry logic**
     *    • If the user is not yet available or a refresh fails, schedules a retry
     *      after 10 seconds (`RETRY_TIMEOUT`).
     * 3. **Tab-visibility awareness**
     *    • Whenever the browser tab regains focus (`visibilitychange → visible`),
     *      the class validates the token immediately to avoid using an expired
     *      credential after a long period in the background.
     * 4. **Re-entrancy protection**
     *    • Concurrent callers share one in-flight renewal and receive its result.
     * 5. **Breadcrumb logging**
     *    • Logs every significant step (`start`, scheduled refresh, retry, success,
     *      failure, visibility change) both to the console and to an external
     *      monitoring utility via `addBreadcrumb`.
     *
     * Implementation Notes
     * --------------------
     * • Uses `setTimeout` instead of `setInterval` so that each new schedule is
     *   created only after the previous refresh attempt finishes.
     * • The constructor is private to enforce the singleton pattern; obtain the
     *   instance via `TokenRefresher.getInstance()`.
     * • All timers are cleared before re-scheduling to prevent multiple pending
     *   callbacks.
     */

    private static instance: TokenRefresher

    private timeoutId: NodeJS.Timeout | null = null
    // The renewal currently in progress, shared by every concurrent caller; null when idle.
    private inFlight: Promise<boolean> | null = null
    private lastRefreshTime: number = 0
    // The token the last successful renewal produced, so `renewRejected` can recognise a
    // credential it has just minted coming back refused.
    private lastMintedToken: string | null = null
    private eventDebounceTimeoutId: NodeJS.Timeout | null = null
    private isIOS: boolean = false

    private constructor() {
        this.isIOS =
            /iPad|iPhone|iPod/.test(navigator.userAgent) ||
            (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
        this.setupEventListeners()
    }

    public static getInstance(): TokenRefresher {
        if (!TokenRefresher.instance) {
            TokenRefresher.instance = new TokenRefresher()
        }
        return TokenRefresher.instance
    }

    public async start() {
        if (this.inFlight) {
            this.log('[TokenRefresher] Already refreshing, skipping start')
            return
        }
        this.log('[TokenRefresher] Starting token refresher')
        await this.refresh()
    }

    /**
     * Renew the Firebase ID token on the schedule: the timers and the visibility events.
     *
     * Resolves true only when this call (or the in-flight one it joined) actually obtained a new
     * token. Every path that declines to renew — the throttle, the Medicore iframe, no signed-in
     * user, a Firebase failure — resolves false, so a caller acting on a 401 can tell a renewal
     * from a no-op. It used to resolve the same way in all of those cases, which left the API
     * client retrying a request with the credential that had just been rejected (PROD-REACT-SX).
     */
    public async refresh(): Promise<boolean> {
        // The throttle is answered per caller, before the shared renewal below. Deciding it inside
        // that renewal would let a throttled timer tick become the promise `renewRejected` joins,
        // handing its "no" to the one caller holding proof that the token needs replacing.
        if (Date.now() - this.lastRefreshTime < MIN_REFRESH_INTERVAL) {
            this.log('Refresh call skipped (refresh is allowed only once per 10 seconds)')
            return false
        }

        return this.share()
    }

    /**
     * Renew after the server refused a credential.
     *
     * Exempt from the throttle, which exists only to stop the timers and visibility events
     * churning: a caller holding a 401 has proof its token needs replacing. It is bounded by what
     * it is told instead, which is why the rejected credential is a parameter and not a flag.
     *
     * @param rejectedToken The credential the server refused, or null when the request carried
     *     none — nothing to compare, so the renewal goes ahead.
     */
    public async renewRejected(rejectedToken: string | null): Promise<boolean> {
        if (rejectedToken !== null) {
            // Another request renewed while this one was in flight. Its token is already in the
            // store, so the retry is worth making and no exchange has to pay for it.
            if (rejectedToken !== useAuthStore.getState().idToken) {
                this.log('Renewal not needed: the rejected credential has already been replaced')
                return true
            }

            // A credential this refresher minted seconds ago and the server refused anyway will
            // not become acceptable by minting another one just like it.
            if (rejectedToken === this.lastMintedToken && Date.now() - this.lastRefreshTime < MIN_REFRESH_INTERVAL) {
                this.log('Renewal skipped: the rejected credential was just minted')
                return false
            }
        }

        return this.share()
    }

    /**
     * Single-flight. Several requests can fail with a 401 at once — an audio batch uploads in
     * parallel — and each used to call in, where all but the first became a no-op that still
     * resolved as though it had renewed. Every caller that reaches here intends to renew, so
     * sharing one renewal gives them all the same true answer.
     */
    private share(): Promise<boolean> {
        this.inFlight ??= this.performRefresh().finally(() => {
            this.inFlight = null
        })
        return this.inFlight
    }

    private async performRefresh(): Promise<boolean> {
        const user = auth.currentUser
        if (!user) {
            this.log('User not ready.')
            this.log('Apps in registry', { app_count: getApps().length })
            this.scheduleRetry()
            return false
        }

        this.lastRefreshTime = Date.now()

        try {
            this.lastMintedToken = await user.getIdToken(true)
            this.log('Token has been refreshed successfully.')
            this.scheduleRefresh()
            return true
        } catch (e: unknown) {
            const code = e instanceof Error && 'code' in e ? (e as { code?: string }).code : undefined
            switch (code) {
                case 'auth/network-request-failed':
                    console.warn('Network request failed:', e)
                    break
                case 'auth/user-token-expired':
                    console.warn('Token expired and cannot be refreshed, so the user is logged out.', e)
                    clearAuth()
                    break
                default:
                    console.error('Refresh failed:', e)
                    break
            }
            if (code !== 'auth/user-token-expired') {
                // if we has cleared the auth state, we should not retry to refresh the token
                this.scheduleRetry()
            }
            return false
        }
    }

    private debouncedRefresh(): void {
        // Clear any existing debounced refresh
        if (this.eventDebounceTimeoutId) {
            clearTimeout(this.eventDebounceTimeoutId)
        }

        // On iOS, focus and visibility events can overlap or fire in rapid succession
        const debounceDelay = this.isIOS ? EVENT_DEBOUNCE_DELAY : 100

        this.eventDebounceTimeoutId = setTimeout(async () => {
            this.eventDebounceTimeoutId = null
            await this.refresh()
        }, debounceDelay)
    }

    private scheduleRefresh(): void {
        this.clearTimeout()
        this.log('Refresh scheduled', { delayMinutes: REFRESH_TIMEOUT / 60000 })
        this.timeoutId = setTimeout(async () => {
            this.timeoutId = null
            await this.refresh()
        }, REFRESH_TIMEOUT)
    }

    private scheduleRetry(): void {
        // Only clear the main timeout, not the debounce timeout
        if (this.timeoutId) {
            clearTimeout(this.timeoutId)
            this.timeoutId = null
        }
        this.log(`Schedule retry`, { retry_timeout_sec: RETRY_TIMEOUT / 1000 })
        this.timeoutId = setTimeout(async () => {
            this.timeoutId = null
            await this.refresh()
        }, RETRY_TIMEOUT)
    }

    private clearTimeout(): void {
        if (this.timeoutId) {
            clearTimeout(this.timeoutId)
            this.timeoutId = null
        }

        if (this.eventDebounceTimeoutId) {
            clearTimeout(this.eventDebounceTimeoutId)
            this.eventDebounceTimeoutId = null
        }
    }

    private setupEventListeners(): void {
        this.log('Setting up event listeners', { isIOS: this.isIOS })

        // Use debounced refresh for event-triggered refreshes to prevent rapid successive calls
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible') {
                this.log('Visibility changed to visible')
                this.debouncedRefresh()
            }
        })

        // On iOS, focus events can fire very frequently during multitasking
        // Use debounced refresh to prevent stack overflow
        window.addEventListener('focus', () => {
            // Only trigger refresh if the tab is visible to avoid hidden background refreshes
            if (document.visibilityState === 'visible') {
                this.log('Focus event received (visible)')
                this.debouncedRefresh()
            }
        })

        // Additional iOS-specific handling for pageshow event
        // This helps with iOS Safari's tab management
        if (this.isIOS) {
            window.addEventListener('pageshow', (event) => {
                this.log('Pageshow (iOS)', { persisted: event.persisted })
                // Only refresh if the page was restored from cache
                if (event.persisted) {
                    this.debouncedRefresh()
                }
            })
        }
    }

    private log(message: string, obj: Record<string, unknown> | null = null) {
        console.info('[TokenRefresher] ' + message, obj || '')
        breadcrumb('TokenRefresher', message, obj ?? {})
    }
}

export const tokenRefresher = TokenRefresher.getInstance()
